/**
 * iOS process-kill route restoration.
 *
 * When iOS terminates a PWA's WebContent process in the background and the
 * user returns, iOS reloads the page as a fresh navigation. React Router
 * starts at the URL the OS remembered — usually the root '/'. Without this
 * hook, the user would land on the login/home page instead of where they were.
 *
 * Strategy:
 *  - On every route change, save the current path+search to localStorage.
 *  - On mount (after PersistGate lifts = user is authenticated), if we detect
 *    a process-kill reload via performance.getEntriesByType('navigation'),
 *    navigate to the saved path.
 *
 * Safe-guards:
 *  - Only restore for authenticated users (redux-persist rehydrated first).
 *  - Never restore public routes (/login, /forgot-password, /auth/*).
 *  - Only restore when navigation type is 'navigate' (fresh load) and
 *    NOT when it's 'reload' (manual F5) or 'back_forward' (bfcache).
 *  - Clear saved route on explicit logout (auth state gone).
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../stores';
import { sessionStateService } from '../services/offline/session-state.service';

const ROUTE_KEY = 'pwa_last_route';

const PUBLIC_PREFIXES = ['/login', '/forgot-password', '/auth/'];

function isPublicRoute(path: string): boolean {
  return PUBLIC_PREFIXES.some((prefix) => path.startsWith(prefix));
}

function isProcessKillReload(): boolean {
  try {
    const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (entries.length === 0) return false;
    // 'navigate' = fresh load (process kill), 'reload' = user-triggered,
    // 'back_forward' = bfcache restore (handled separately via pageshow)
    return entries[0].type === 'navigate';
  } catch {
    return false;
  }
}

export function useRouteRestoration(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state: RootState) => !!state.auth.accessToken);
  const restored = useRef(false);

  // Attempt route restoration once on mount (after PersistGate lifts).
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;

    if (!isAuthenticated) return;
    if (!isProcessKillReload()) return;

    const savedRoute = localStorage.getItem(ROUTE_KEY);
    if (!savedRoute) return;

    // Don't restore if already on a meaningful route (not root and not public).
    const currentPath = window.location.pathname;
    if (!isPublicRoute(currentPath) && currentPath !== '/') return;

    // Don't restore to a public route (e.g. user had previously visited /login).
    if (isPublicRoute(savedRoute)) return;

    navigate(savedRoute, { replace: true });

    // Restore scroll position from IDB session state (saved by usePageLifecycle).
    sessionStateService.load().then((state) => {
      if (state && state.scrollY > 0) {
        setTimeout(() => window.scrollTo(0, state.scrollY), 150);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Persist current route on every navigation.
  useEffect(() => {
    const path = location.pathname + location.search;
    if (!isPublicRoute(path)) {
      try {
        localStorage.setItem(ROUTE_KEY, path);
      } catch {
        // ignore
      }
    }
  }, [location.pathname, location.search]);

  // Clear saved route on logout.
  useEffect(() => {
    if (!isAuthenticated) {
      try {
        localStorage.removeItem(ROUTE_KEY);
      } catch {
        // ignore
      }
    }
  }, [isAuthenticated]);
}
